// frontend/src/components/NotificationToast.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Bell, Trophy } from 'lucide-react';
import { useSocket } from '../contexts/SocketContext';

const NotificationToast = ({ notification, onClose }) => {
    const navigate = useNavigate();
    const { markAsRead, deleteNotification, markTournamentNotificationsAsRead } = useSocket();
    const [isExiting, setIsExiting] = useState(false);

    // Duración según el tipo de notificación
    const NOTIFICATION_DURATION = {
        'TOURNAMENT_STARTING_SOON': 20000, // 20 segundos
        'TOURNAMENT_STARTED': 15000,       // 15 segundos
        'TOURNAMENT_CANCELLED': 10000,     // 10 segundos
        'DEFAULT': 7000                    // 7 segundos por defecto
    };

    useEffect(() => {
        const duration = NOTIFICATION_DURATION[notification.type] || NOTIFICATION_DURATION.DEFAULT;
        const timer = setTimeout(() => {
            handleClose();
        }, duration);

        return () => clearTimeout(timer);
    }, [notification.id]);

    const handleClose = () => {
        setIsExiting(true);
        setTimeout(() => {
            onClose(notification.id);
        }, 300); // duración de la animación
    };

    const handleClick = () => {
        if (notification.link) {
            // Si es una notificación de torneo, eliminar todas las del mismo torneo
            if (notification.tournamentId) {
                markTournamentNotificationsAsRead(notification.tournamentId);
                console.log(`🏆 Navegando a torneo ${notification.tournamentId} - eliminando todas sus notificaciones`);
            } else {
                // Para notificaciones que no son de torneo, comportamiento normal
                markAsRead(notification.id);
                deleteNotification(notification.id);
            }
            
            // Cerrar el toast visualmente
            handleClose();
            
            // Navegar con estado para indicar origen de notificación
            navigate(notification.link, { 
                state: { fromNotification: true }
            });
        }
    };

    const getIcon = () => {
        switch (notification.type) {
            case 'TOURNAMENT_STARTING_SOON':
            case 'TOURNAMENT_STARTED':
            case 'TOURNAMENT_CANCELLED':
                return <Trophy className="w-5 h-5" />;
            default:
                return <Bell className="w-5 h-5" />;
        }
    };

    return (
        <div
            className={`fixed w-full max-w-sm transform transition-all duration-300 ease-in-out ${
                isExiting ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'
            }`}
        >
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                {/* Barra de progreso */}
                <div className="absolute bottom-0 left-0 h-1 bg-primary-light transition-all duration-300"
                     style={{
                         width: '100%',
                         animationName: 'progress',
                         animationDuration: `${NOTIFICATION_DURATION[notification.type] || NOTIFICATION_DURATION.DEFAULT}ms`,
                         animationTimingFunction: 'linear',
                     }} />

                {/* Contenido de la notificación */}
                <div className="p-4 flex items-start">
                    {/* Icono */}
                    <div className="flex-shrink-0 pt-0.5">
                        <div className="h-10 w-10 bg-primary bg-opacity-10 rounded-full flex items-center justify-center text-primary">
                            {getIcon()}
                        </div>
                    </div>

                    {/* Contenido */}
                    <div className="ml-3 w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">
                            {notification.title}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                            {notification.message}
                        </p>
                        {notification.link && (
                            <button
                                onClick={handleClick}
                                className="mt-2 text-sm font-medium text-primary hover:text-primary-dark"
                            >
                                Ver detalles
                            </button>
                        )}
                    </div>

                    {/* Botón de cerrar */}
                    <div className="ml-4 flex-shrink-0 flex">
                        <button
                            onClick={handleClose}
                            className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        >
                            <span className="sr-only">Cerrar</span>
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotificationToast;